import React from 'react';
import { LucideIcon, Plus } from 'lucide-react';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  iconColor?: 'teal' | 'amber' | 'cyan' | 'purple' | 'rose' | 'indigo';
  variant?: 'default' | 'compact';
}

const colorMap = {
  teal:   { iconBg: 'bg-teal-500', shadow: 'shadow-teal-300/40', soft: 'bg-teal-50', softText: 'text-teal-700' },
  amber:  { iconBg: 'bg-amber-500', shadow: 'shadow-amber-300/40', soft: 'bg-amber-50', softText: 'text-amber-700' },
  cyan:   { iconBg: 'bg-cyan-500', shadow: 'shadow-cyan-300/40', soft: 'bg-cyan-50', softText: 'text-cyan-700' },
  purple: { iconBg: 'bg-purple-500', shadow: 'shadow-purple-300/40', soft: 'bg-purple-50', softText: 'text-purple-700' },
  rose:   { iconBg: 'bg-rose-500', shadow: 'shadow-rose-300/40', soft: 'bg-rose-50', softText: 'text-rose-700' },
  indigo: { iconBg: 'bg-indigo-500', shadow: 'shadow-indigo-300/40', soft: 'bg-indigo-50', softText: 'text-indigo-700' },
};

const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  iconColor = 'teal',
  variant = 'default',
}) => {
  const colors = colorMap[iconColor];

  if (variant === 'compact') {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <div className={`w-14 h-14 ${colors.soft} rounded-2xl flex items-center justify-center mb-3`}>
          <Icon className={`w-6 h-6 ${colors.softText}`} strokeWidth={1.8} />
        </div>
        <h3 className="text-sm font-bold text-gray-700 mb-1">{title}</h3>
        {description && (
          <p className="text-xs text-gray-500 max-w-xs">{description}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-16 lg:py-20">
      {/* İkon container */}
      <div className="relative mb-6">
        {/* Subtle pulse halka */}
        <span className={`absolute inset-0 ${colors.soft} rounded-3xl animate-ping opacity-50`} />

        <div className={`relative w-20 h-20 ${colors.iconBg} rounded-3xl flex items-center justify-center shadow-lg ${colors.shadow}`}>
          <Icon className="w-10 h-10 text-white" strokeWidth={1.8} />
        </div>
      </div>

      <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">{title}</h3>

      {description && (
        <p className="text-sm text-gray-500 max-w-md mb-6 leading-relaxed">
          {description}
        </p>
      )}

      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className={`inline-flex items-center gap-2 px-5 py-2.5 ${colors.iconBg} text-white rounded-xl font-semibold shadow-lg ${colors.shadow} hover:scale-105 hover:-translate-y-0.5 transition-all`}
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
